import { v4 as uuidv4 } from "uuid";
import { constructPathURL } from "../utils/constructPathURL.js";
import {
    CHAT_SESSION_ID,
    SOURCE_CHAT,
    IS_LOGIN,
    KEYS,
    ERROR_MSG,
    CLIENT_SECRET,
    FIVE_MIN,
} from "../../constants.js";
import { formatMessageData, formatMessageDataV2 } from "../utils/formatData.js";
import {
    concatMessage,
    setLoadingReply,
    setMessages,
    setIsqrLoading,
} from "../features/messagesSlice.js";
import store from "../app/store.js";
import { resetSelfService, setSelfService } from "../features/sessionSlice.js";
import intentCopywriting from "../utils/intentCopywriting.js";
import { setIsTypingContainerState } from "../features/componentStateSlice.js";
import { getDecryptedStorage } from "../utils/crypto.js";
import { setQrcodeUrl } from "../features/sessionSlice.js";

export async function requestChat() {
    const requestChatUrl = constructPathURL("/v2/marina/initiateChat");
    const deviceId = uuidv4();
    const timeNow = new Date();
    const expiredAt = new Date(timeNow.getTime() + 6 * 60000);

    const requestData = {
        channel: SOURCE_CHAT,
        deviceId: deviceId,
        clientId: "1da218fb588753921da37d1b10ad0551",
    };

    const response = await fetch(requestChatUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
    });

    if (!response.ok) {
        throw new Error("Request response was not ok");
    }

    const responseData = await response.json();

    if (responseData.code !== "0000") {
        throw new Error(
            "Request response was not success",
            responseData.message
        );
    } else {
        return { ...responseData, deviceId: requestData.deviceId, expiredAt };
    }
}

export async function generatePreLoginToken(correlationId) {
    const tokenURL = constructPathURL("/marina/token");

    const tokenData = {
        client_id: "1da218fb588753921da37d1b10ad0551",
        channel: SOURCE_CHAT,
    };

    const response = await fetch(tokenURL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-correlation-id": correlationId,
        },
        body: JSON.stringify(tokenData),
    });

    if (!response.ok) {
        if (response.status === 401) {
            await requestChat();
        } else {
            throw new Error("Generate token response was not ok");
        }
    }
    return response.json();
}

export async function generatePostLoginToken(correlationId) {
    const tokenURL = constructPathURL("/postauth/v2/marina/token");

    const tokenData = {
        client_id: "1da218fb588753921da37d1b10ad0551",
        channel: SOURCE_CHAT,
        chat_id: CHAT_SESSION_ID,
        client_secret: CLIENT_SECRET,
        sessionId: "43432434324",
    };

    const response = await fetch(tokenURL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-correlation-id": correlationId,
        },
        body: JSON.stringify(tokenData),
    });

    if (!response.ok) {
        if (response.status === 401) {
            await requestChat();
        } else {
            throw new Error("Generate token response was not ok");
        }
    }
    const responseData = await response.json();
    return responseData;
}

export async function sendChat(
    deviceId,
    token,
    sKey,
    content,
    position,
    dispatch
) {
    dispatch(setIsTypingContainerState(true));
    const sendChatUrl = constructPathURL("/marina/api/v3/chat");

    const sendData = {
        content: typeof content === "string" ? content : content.accountNumber,
        channel: SOURCE_CHAT,
        chat_id: CHAT_SESSION_ID,
        chat_type: "text",
        request_id: uuidv4(),
        self_service: store.getState().session.selfService,
        selected: typeof content === "string" ? {} : content,
    };

    try {
        const sendResponse = await fetch(sendChatUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(sendData),
        });

        if (!sendResponse.ok) {
            // Regenerate token if token invalid
            if (sendResponse.status === 401) {
                const newRequestResponse = await requestChat();
                const newTokenResponse = await generatePostLoginToken(
                    newRequestResponse.deviceId,
                    newRequestResponse.correlation_id
                );
                await sendChat(
                    deviceId,
                    newTokenResponse.token,
                    newTokenResponse.s_key,
                    content,
                    position,
                    dispatch
                );
            } else {
                dispatch(setIsTypingContainerState(false));
                const botReply = formatMessageDataV2(
                    deviceId,
                    "bot",
                    ERROR_MSG,
                    position,
                    true
                );
                dispatch(concatMessage(botReply));
                //* Commented code below because want to keep conversation alive
                // throw new Error("Send response was not ok");
            }
        }

        const cloned = sendResponse.clone();
        const reader = sendResponse.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let result = "";
        let token_position = 0;
        let isStreamed = false;

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                if (IS_LOGIN) {
                    if (!isStreamed) {
                        const clonedResponse = await cloned.json();
                        if (clonedResponse.data.selection) {
                            const botReply = formatMessageData(
                                deviceId,
                                "bot",
                                "Please select an account number.",
                                position
                            );
                            dispatch(concatMessage(botReply));
                            clonedResponse.data.selection.push({
                                productName:
                                    "Not seeing your card? Click here.",
                                accountNumber: "not_found",
                            });
                            const selectionMessage = formatMessageData(
                                deviceId,
                                "bot",
                                clonedResponse.data.selection,
                                position,
                                "selection"
                            );
                            dispatch(concatMessage(selectionMessage));
                            dispatch(
                                setSelfService(clonedResponse.data.self_service)
                            );
                            dispatch(setLoadingReply(true));
                        } else if (clonedResponse.data.data.length !== 0) {
                            const intentResponse = await intentCopywriting(
                                clonedResponse.data.self_service.intent,
                                clonedResponse.data.data,
                                content
                            );
                            const botReply = formatMessageData(
                                deviceId,
                                "bot",
                                intentResponse,
                                position
                            );
                            dispatch(concatMessage(botReply));
                            dispatch(resetSelfService());
                        }
                    }
                }
                break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const chunk_obj = chunk.split("\n");

            dispatch(setIsTypingContainerState(false));

            try {
                const jsonObjects = chunk_obj
                    .filter((item) => item != "")
                    .map((item) => JSON.parse(item));

                jsonObjects.forEach((jsonObject) => {
                    if (jsonObject.done) {
                        dispatch(setLoadingReply(false));
                        return;
                    }

                    const currentMessages = store.getState().messages.messages;
                    let updatedMessages = [...currentMessages];

                    if (token_position == 0) {
                        const tempMessage = formatMessageData(
                            jsonObject.conversation_id,
                            "bot",
                            jsonObject.response,
                            position
                        );
                        updatedMessages.push(tempMessage);
                    } else {
                        const latestMessage = {
                            ...updatedMessages[updatedMessages.length - 1],
                        };
                        latestMessage.content += jsonObject.response;
                        updatedMessages[updatedMessages.length - 1] =
                            latestMessage;
                    }
                    token_position++;
                    dispatch(setMessages(updatedMessages));
                    dispatch(resetSelfService());

                    if (jsonObject.response) {
                        result += jsonObject.response;
                        isStreamed = true;
                    }
                });
            } catch (e) {
                console.error("Error parsing JSON chunk:", e);
            }
        }
        return result;
    } catch (error) {
        throw new Error("Send response was not success", error);
    }
}

export async function sendChatPrelogin(
    deviceId,
    token,
    sKey,
    content,
    position,
    dispatch
) {
    dispatch(setIsTypingContainerState(true));
    const sendChatUrl = constructPathURL("/marina/api/prelogin");

    const sendData = {
        content: typeof content === "string" ? content : content.accountNumber,
        channel: SOURCE_CHAT,
        chat_id: CHAT_SESSION_ID,
        chat_type: "text",
        request_id: uuidv4(),
        self_service: store.getState().session.selfService,
        selected: typeof content === "string" ? {} : content,
    };

    try {
        const sendResponse = await fetch(sendChatUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(sendData),
        });

        if (!sendResponse.ok) {
            // Regenerate token if token invalid
            if (sendResponse.status === 401) {
                const newRequestResponse = await requestChat();
                const newTokenResponse = await generatePreLoginToken(
                    newRequestResponse.deviceId,
                    newRequestResponse.correlation_id
                );
                await sendChatPrelogin(
                    deviceId,
                    newTokenResponse.token,
                    newTokenResponse.s_key,
                    content,
                    position,
                    dispatch
                );
            } else {
                dispatch(setIsTypingContainerState(false));
                const botReply = formatMessageDataV2(
                    deviceId,
                    "bot",
                    ERROR_MSG,
                    position,
                    true
                );
                dispatch(concatMessage(botReply));

                //* Commented code below because want to keep conversation alive
                // throw new Error("Send response was not ok");
            }
        }

        const cloned = sendResponse.clone();
        const reader = sendResponse.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let result = "";
        let token_position = 0;
        let isStreamed = false;

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                if (IS_LOGIN) {
                    if (!isStreamed) {
                        const clonedResponse = await cloned.json();
                        if (clonedResponse.data.selection) {
                            const botReply = formatMessageData(
                                deviceId,
                                "bot",
                                "Please select an account number.",
                                position
                            );
                            dispatch(concatMessage(botReply));
                            clonedResponse.data.selection.push({
                                productName:
                                    "Not seeing your card? Click here.",
                                accountNumber: "not_found",
                            });
                            const selectionMessage = formatMessageData(
                                deviceId,
                                "bot",
                                clonedResponse.data.selection,
                                position,
                                "selection"
                            );
                            dispatch(concatMessage(selectionMessage));
                            dispatch(
                                setSelfService(clonedResponse.data.self_service)
                            );
                            dispatch(setLoadingReply(true));
                        } else if (clonedResponse.data.data.length !== 0) {
                            const intentResponse = await intentCopywriting(
                                clonedResponse.data.self_service.intent,
                                clonedResponse.data.data,
                                content
                            );
                            const botReply = formatMessageData(
                                deviceId,
                                "bot",
                                intentResponse,
                                position
                            );
                            dispatch(concatMessage(botReply));
                            dispatch(resetSelfService());
                        }
                    }
                }
                break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const chunk_obj = chunk.split("\n");

            // Seems like status response come first, then take sometimes to get content response (API)
            dispatch(setIsTypingContainerState(false));

            try {
                const jsonObjects = chunk_obj
                    .filter((item) => item != "")
                    .map((item) => JSON.parse(item));

                jsonObjects.forEach((jsonObject) => {
                    if (jsonObject.done) {
                        dispatch(setLoadingReply(false));
                        return;
                    }

                    const currentMessages = store.getState().messages.messages;
                    let updatedMessages = [...currentMessages];

                    if (token_position == 0) {
                        const tempMessage = formatMessageData(
                            jsonObject.conversation_id,
                            "bot",
                            jsonObject.response,
                            position
                        );
                        updatedMessages.push(tempMessage);
                    } else {
                        const latestMessage = {
                            ...updatedMessages[updatedMessages.length - 1],
                        };
                        latestMessage.content += jsonObject.response;
                        updatedMessages[updatedMessages.length - 1] =
                            latestMessage;
                    }

                    token_position++;
                    dispatch(setMessages(updatedMessages));
                    dispatch(resetSelfService());

                    if (jsonObject.response) {
                        result += jsonObject.response;
                        isStreamed = true;
                    }
                });
            } catch (e) {
                console.error("Error parsing JSON chunk:", e);
            }
        }
        return result;
    } catch (error) {
        throw new Error("Send response was not success", error);
    }
}

export async function endChat(token) {
    const endChatUrl = constructPathURL("/marina/api/endChat");

    const response = await fetch(endChatUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error("End chat was not successful");
    }

    return response.json();
}

export async function queue(token, chat_id) {
    const queueUrl = constructPathURL("/marina/api/queue");

    const queueData = JSON.stringify({
        chat_id,
        tenant: "Environment",
    });

    return fetch(queueUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: queueData,
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error("Queue response was not ok");
            }
            return response.json();
        })
        .then((result) => {
            if (result.code) {
                throw new Error("Can't get queue position. Please try again.");
            }
            return result;
        })
        .catch((error) => {
            console.error("Fetch error:", error);
            throw error;
        });
}

export const createHistoryChat = async (
    token,
    question,
    answer,
    prelogin_session,
    chat_type = "ai_assistance",
    request_id = "3432432432423"
) => {
    const createHistoryChatUrl = constructPathURL("/marina/api/chat-history");

    const createHistoryChatData = JSON.stringify({
        question,
        answer,
        chat_type,
        request_id,
        prelogin_session,
    });

    try {
        const res = await fetch(createHistoryChatUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: createHistoryChatData,
        });

        return res;
    } catch (error) {
        console.log("error", error);
    }
};

export const getHistoryChat = async (token) => {
    const getHistoryChatUrl = constructPathURL("/marina/api/chat-history");

    try {
        const res = await fetch(getHistoryChatUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        return res.json();
    } catch (error) {
        console.log("error", error);
    }
};

export const rateConversation = async (token, type, id) => {
    const apiUrl = constructPathURL("/marina/api/feedback");

    const payload = {
        conversation_id: id,
        thumbs_up: type,
    };

    try {
        const res = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        await res.json();
    } catch (error) {
        console.log(error);
    }
};

export const generateFirstQRcode = async (opts) => {
    const mockAPIUrl = "http://localhost:5555/qr1";

    const payload = {
        chatId: "123abc456def",
    };

    const res = await fetch(mockAPIUrl, {
        method: "POST",
        headers: {
            "Content-type": "application/json",
        },
        body: JSON.stringify(payload),
        signal: opts?.signal,
    });

    if (!res.ok) throw new Error(`qr1 http ${res.status}`);
    return res.json();
};

export const generateSecondQRcode = async (opts) => {
    const mockAPIUrl = "http://localhost:5555/qr2";

    const payload = {
        chatId: "123abc456def",
    };

    const res = await fetch(mockAPIUrl, {
        method: "POST",
        headers: {
            "Content-type": "application/json",
        },
        body: JSON.stringify(payload),
        signal: opts?.signal,
    });

    if (!res.ok) throw new Error(`qr2 http ${res.status}`);
    return res.json();
};

export const checkStatusQRcode = async () => {
    const mockAPIUrl = "http://localhost:5555/check-status";

    const payload = {
        chatId: "123abc456def",
    };

    const res = await fetch(mockAPIUrl, {
        method: "POST",
        headers: {
            "Content-type": "application/json",
        },
        body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`Status QR ${res.status}`);
    return res.json();
};

export const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export const handleFirstQRcode = async (dispatch) => {
    console.log("first qrcode");
    let mockCount = 0;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
        controller.abort(
            new DOMException("QR1 timed out after 5 minutes", "AbortError")
        );
    }, FIVE_MIN);
    return new Promise((resolve) => {
        setTimeout(async () => {
            try {
                while (true) {
                    try {
                        const res = await generateFirstQRcode({
                            signal: controller.signal,
                        });
                        dispatch(setIsqrLoading(false));
                        dispatch(setQrcodeUrl(res?.url));
                    } catch (error) {
                        dispatch(setIsqrLoading(false));
                        if (error?.name === "AbortError") {
                            throw new Error(
                                "QR1 session expired. Please start again."
                            );
                        }
                        throw error; // other network errors
                    }
                    mockCount++;
                    console.log("QR1 attempt:", mockCount);
                    // mock real flow, prod api take time to return respond
                    if (mockCount > 5) break;
                    await wait(1500);
                }
            } finally {
                resolve();
                dispatch(setIsqrLoading(false));
                clearTimeout(timeoutId);
            }
        });
    });
};

export const handleSecondQRcode = async (dispatch) => {
    console.log("second qrcode");
    let mockCount = 0;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
        controller.abort(
            new DOMException("QR1 timed out after 5 minutes", "AbortError")
        );
    }, FIVE_MIN);

    try {
        while (true) {
            try {
                const res = await generateSecondQRcode({
                    signal: controller.signal,
                });
                dispatch(setQrcodeUrl(res?.url));
            } catch (error) {
                if (error?.name === "AbortError") {
                    throw new Error("QR2 session expired. Please start again.");
                }
                throw error; // other network errors
            }
            mockCount++;
            console.log("QR2 attempt:", mockCount);
            // mock real flow, prod api take time to return respond
            if (mockCount > 5) break;
            await wait(1500);
        }
    } finally {
        clearTimeout(timeoutId);
    }
};

export const handleCheckStatusQrcode = async (dispatch) => {
    console.log("check status...");
    let statusCount = 0;

    // mock real flow, prod api take time to return respond
    while (true) {
        const res = await checkStatusQRcode();
        console.log("Status QR", res);
        statusCount++;
        if (statusCount > 5) break;

        await wait(2000);
    }
};
